import { useEffect, RefObject } from 'react'

/**
 * Custom hook to detect clicks outside of a specified element.
 * * @param ref A React ref object pointing to the container element.
 * @param handler The function to call when a click outside is detected.
 */
const useClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const listener = (event: MouseEvent | TouchEvent) => {
      // Do nothing if clicking ref's element or descendant elements
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return
      }

      // Call the handler function if the click is outside
      handler(event)
    }

    // Attach the event listener to the document
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler]) // Re-run if ref or handler changes (though ref usually won't)
}

export default useClickOutside
